import { useMemo, useState } from "react";
import {
  calculateDataTableState,
  constructQueryParamsString,
  getSortDirection,
  handlePageSizeChange,
  showErrorToast,
  translateSubElementString,
} from "../../../../../../utils";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Box, Button, Checkbox, Flex, useToast } from "@chakra-ui/react";
import {
  useChangeReportExclusion,
  useGetSuppliersYearByYearAverageData,
  useGetSuppliersYearByYearData,
  useGetSuppliersYearlyData,
} from "../../../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { DataTable } from "../../../../../../components/DataTable";
import * as React from "react";
import { MultiSelect } from "../../../../../../components";

const DEFAULT_YEARS = ["2021", "2022", "2023", "2024"];

const DataQuality = ({pageSize=20}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "suppliersEmissionsDashboard.emissionsByYear",
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);
  const [years, setYears] = useState(DEFAULT_YEARS);

  const getQueryParamsString = () => {
    const queryParams = {
      years: years.length !== 0 ? years : DEFAULT_YEARS,
      showExcluded: true,
    };
    return constructQueryParamsString(queryParams);
  };

  const { mutate: changeReportExclusion } = useChangeReportExclusion({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSuccess: (_, variables) => {
      setYearByYearEmissionsData((prevData) =>
        prevData.map((item) =>
          item.supplierId === variables.supplierId ? { ...item, excludeFromReport: variables.excludeFromReport } : item
        )
      );
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      setYearByYearEmissionsData(data);
      // handlePageSizeChange(page, queryPageIndex, manageDataTableState);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const dynamicEmissionsColumns = years.flatMap((year) => [
    {
      header: () => (
        <>
          {year} {translateSubElementString("suppliersEmissionsDashboard.emissionsByYear.gridHeaders.emissions")}
        </>
      ),
      accessorKey: `emissions-${year}`,
      size: 100,
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;
        const b = rowB.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;
        return a - b;
      },
      cell: ({ row }) => row.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0,
    },
  ]);

  const columns = useMemo(() => {
    const handleExcludeFromReport = (id, excludeFromReport) => {
      const data = {
        supplierId: id,
        excludeFromReport: !excludeFromReport,
      };
      changeReportExclusion(data);
    };

    return [
      {
        header: t("gridHeaders.supplierName"),
        accessorKey: "supplierName",
        size: 120,
      },

      ...dynamicEmissionsColumns,
      {
        header: t("gridHeaders.dataQuality"),
        accessorKey: "dataQuality",
        enableColumnFilter: true,
      },
      {
        header: "Target Commitment",
        accessorKey: "targetCommitmentBool",
        enableColumnFilter: true,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.targetCommitment;
          const b = rowB.original.targetCommitment;
          return a - b;
        },
        cell: ({ row }) => (row.original?.targetCommitment === true ? "Yes" : "No"),
      },
      {
        header: "Exclude from Report",
        accessorKey: "excludeFromReportCheckbox",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { excludeFromReport } = row.original;

          return (
            <Checkbox
              isChecked={excludeFromReport}
              onChange={(e) => handleExcludeFromReport(row.original?.supplierId, excludeFromReport)}
            />
          );
        },
      },
    ];
  }, [t, dynamicEmissionsColumns, changeReportExclusion]);

  return (
    <Flex flexFlow={"column"} gap={4} marginBlock={4}>
      <Flex align="center" gap={2}>
        <Box as="label" fontWeight="bold" fontSize="sm" color="gray.600">
          Year:
        </Box>
        <Box minW="250px">
          <MultiSelect
            onChange={(value) => setYears(value.map((item) => item.value))}
            options={Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() + 1 - i;
              return { label: year.toString(), value: year.toString() };
            })}
            isMulti={true}
            defaultValue={years.map((year) => ({ label: year, value: year }))}
          />
        </Box>
      </Flex>
      <DataTable
        data={yearByYearEmissionsData}
        columns={columns}
        isLoading={isFetching}
        serverSidePagination={false}
        enableSorting={true}
        enableFilters={true}
        queryPageSize={pageSize}
      />
    </Flex>
  );
};

export default DataQuality;
